import React from 'react';
import DOMPurify from 'dompurify';

// React Error Boundary with Auto-Recovery
export class ErrorBoundaryWithRecovery extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      recoveryAttempts: 0,
    };
    this.maxRecoveryAttempts = 3;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Report to error monitor
    if (window.errorMonitor) {
      window.errorMonitor.handleError({
        type: 'react_error_boundary',
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        componentName: this.props.componentName || 'UnknownComponent',
      });
    }

    // Attempt auto-recovery
    if (this.state.recoveryAttempts < this.maxRecoveryAttempts) {
      setTimeout(() => {
        this.attemptRecovery();
      }, 1000);
    }
  }

  attemptRecovery = () => {
    console.log(
      `🔄 ErrorBoundary: Attempting auto-recovery (attempt ${this.state.recoveryAttempts + 1})`
    );

    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      recoveryAttempts: prevState.recoveryAttempts + 1,
    }));

    // Notify parent component of recovery attempt
    if (this.props.onRecoveryAttempt) {
      this.props.onRecoveryAttempt(this.state.recoveryAttempts + 1);
    }
  };

  // Advanced recovery strategies after auto-recovery fails
  handleAdvancedRecovery = async strategy => {
    console.log(`🚀 Advanced recovery strategy: ${strategy}`);

    try {
      switch (strategy) {
        case 'ask-ai':
          // Ask AI for help with the error
          await this.askAIForHelp();
          break;

        case 'reset-state':
          // Clear all localStorage and sessionStorage
          localStorage.clear();
          sessionStorage.clear();

          // Show confirmation and reload
          this.showModalConfirmation(
            '🔄 State Reset Complete',
            'All application data has been cleared. The page will now reload with fresh state.',
            () => {
              this.setState({
                hasError: false,
                error: null,
                errorInfo: null,
                recoveryAttempts: 0,
              });
              setTimeout(() => window.location.reload(), 1000);
            }
          );
          break;

        case 'safe-mode':
          // Set safe mode flag and reload
          localStorage.setItem('safeMode', 'true');
          this.showModalConfirmation(
            '🛡️ Safe Mode Activated',
            'The application will reload with minimal features to avoid conflicts. You can exit safe mode from settings.',
            () => window.location.reload()
          );
          break;

        case 'clear-cache':
          // Clear browser cache and reload
          this.showModalConfirmation(
            '🧹 Clearing Cache...',
            'Browser cache will be cleared and the page will reload. This may take a moment.',
            async () => {
              if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map(name => caches.delete(name)));
              }
              window.location.reload();
            }
          );
          break;

        case 'copy-error':
          // Generate and copy detailed diagnostic report
          this.copyErrorReport();
          break;

        default:
          window.location.reload();
      }
    } catch (recoveryError) {
      console.error('Advanced recovery failed:', recoveryError);
      // Fallback to page reload
      window.location.reload();
    }
  };

  // Ask AI (Leo) for help with the error
  // Ask AI (Leo) for help with the error
  askAIForHelp = async () => {
    // Scan for current JavaScript errors
    const jsErrors = this.scanForJavaScriptErrors();

    const errorContext = {
      error: this.state.error?.message,
      stack: this.state.error?.stack?.split('\n').slice(0, 5).join('\n'),
      componentName: this.props.componentName,
      url: window.location.href,
      userAgent: navigator.userAgent.split(' ').slice(-2).join(' '),
      timestamp: new Date().toISOString(),
      recoveryAttempts: this.state.recoveryAttempts,
      buildStatePatterns: this.getBuildStatePatterns(),
      additionalErrors: jsErrors,
      detailedAttempts: this.state.recoveryAttempts.map((attempt, index) => ({
        attempt: index + 1,
        strategy: attempt.strategy || 'unknown',
        result: attempt.success ? 'success' : 'failed',
        error: attempt.error?.message || 'no error details',
        timestamp: attempt.timestamp,
      })),
    };

    // Show loading modal
    this.showModalConfirmation(
      '🤖 Consulting AI Assistant...',
      'Leo is analyzing the error and checking against known patterns. This may take a moment.',
      null,
      null,
      true
    );

    try {
      const response = await fetch('/api/together-complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content:
                'You are Leo, an AI assistant helping with error recovery in a React application. CONTEXT: This is a production CondoShield CRM application with error monitoring. The user has experienced an error that auto-recovery could not fix after 3 attempts. TASK: Analyze the error and provide ONE of these responses: 1. "Developer interaction needed." - If this requires code changes 2. A concise, actionable solution (2-3 sentences max) - If user can fix it. You will receive detailed information about what recovery attempts were tried and what additional JavaScript errors are present. PATTERNS TO CHECK: Look for known patterns from buildstate that might help identify the issue. Be direct and helpful. No fluff. Explain what the 3 attempts were and why they failed if you can determine it.',
            },
            {
              role: 'user',
              content: `Error Analysis Request:

PRIMARY ERROR: ${errorContext.error}
STACK: ${errorContext.stack}
COMPONENT: ${errorContext.componentName}

RECOVERY ATTEMPTS MADE:
${errorContext.detailedAttempts.map(a => `${a.attempt}. ${a.strategy} - ${a.result} (${a.error})`).join('\n')}

ADDITIONAL JS ERRORS DETECTED:
${jsErrors.length > 0 ? jsErrors.map(e => `- ${e.type}: ${e.message}`).join('\n') : 'None detected'}

BUILDSTATE PATTERNS: ${JSON.stringify(errorContext.buildStatePatterns)}

Please analyze and provide recovery advice.`,
            },
          ],
          model: 'meta-llama/Llama-3.3-70B-Instruct-Turbo',
          max_tokens: 800,
          temperature: 0.3,
        }),
      });

      if (!response.ok) {
        throw new Error('AI service unavailable');
      }

      const data = await response.json();
      const aiAdvice =
        data.choices?.[0]?.message?.content || 'AI assistant is currently unavailable.';

      // Close loading modal and show AI advice
      const closeElements = document.querySelectorAll('[onclick="window.modalCloseAction()"]');
      closeElements.forEach(el => el.click());

      this.showModalConfirmation(
        "🤖 Leo's Analysis",
        aiAdvice,
        aiAdvice.includes('Developer interaction needed')
          ? null
          : () => {
              if (
                aiAdvice.toLowerCase().includes('refresh') ||
                aiAdvice.toLowerCase().includes('reload')
              ) {
                window.location.reload();
              } else if (aiAdvice.toLowerCase().includes('cache')) {
                this.handleAdvancedRecovery('clear-cache');
              } else if (aiAdvice.toLowerCase().includes('reset')) {
                this.handleAdvancedRecovery('reset-state');
              }
            },
        `Detailed Error Context:\n${JSON.stringify(errorContext, null, 2)}`
      );

      this.setState(prevState => ({
        aiConsultationCount: (prevState.aiConsultationCount || 0) + 1,
        lastAIAdvice: {
          timestamp: new Date().toISOString(),
          advice: aiAdvice,
          context: errorContext,
        },
      }));

      return aiAdvice;
    } catch (error) {
      console.error('AI consultation failed:', error);

      const closeElements = document.querySelectorAll('[onclick="window.modalCloseAction()"]');
      closeElements.forEach(el => el.click());

      this.showModalConfirmation(
        '❌ AI Consultation Failed',
        `Could not reach AI assistant: ${error.message}\n\nPlease try manual recovery or contact support.`,
        null,
        `Technical Error:\n${error.stack || error.message}`
      );

      return 'AI consultation unavailable - manual intervention required';
    }
  };

  // Get relevant buildstate patterns for error analysis
  getBuildStatePatterns = () => {
    try {
      // Get common error patterns from buildstate or localStorage
      const patterns = [
        'React StrictMode compatibility issues',
        'Memory usage optimization patterns',
        'Network error handling improvements',
        'Component lifecycle error patterns',
        'State management error patterns',
      ];

      return patterns.join('\n- ');
    } catch (error) {
      console.warn('Could not access buildstate patterns:', error);
      return 'No patterns available';
    }
  };

  // Copy error report with modal confirmation
  copyErrorReport = () => {
    // Generate Leo's simple explanation based on error type
    const generateLeoExplanation = () => {
      const errorMessage = this.state.error?.message || '';

      if (errorMessage.includes('before initialization')) {
        return {
          summary:
            "A function is being used before it's created in the code. This is like trying to call someone before you have their phone number.",
          technicalContext: 'JavaScript hoisting issue - function referenced before declaration',
          userAction:
            'This requires a code fix by the developer to move function definitions earlier in the component.',
          sessionContext: `User was on ${window.location.pathname} when the error occurred. Component: ${this.props.componentName || 'Unknown'}. Recovery failed after ${this.state.recoveryAttempts} attempts.`,
        };
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        return {
          summary:
            "The app can't connect to the server right now. Think of it like a phone call that won't go through.",
          technicalContext: 'Network connectivity or API endpoint issue',
          userAction:
            'Check your internet connection and try refreshing the page. If it persists, there may be a server issue.',
          sessionContext: `User was trying to access ${window.location.pathname}. Network conditions: ${navigator.connection?.effectiveType || 'unknown'}.`,
        };
      } else if (
        errorMessage.includes('Cannot read property') ||
        errorMessage.includes('undefined')
      ) {
        return {
          summary:
            "The app is trying to use some information that isn't available yet. Like looking for a book that isn't on the shelf.",
          technicalContext: 'Null/undefined property access - likely data loading issue',
          userAction:
            'Try refreshing the page or clearing your browser cache. The data might not have loaded properly.',
          sessionContext: `User was working in ${this.props.componentName || 'unknown component'} on ${window.location.pathname}. Memory usage: ${performance.memory ? `${((performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100).toFixed(1)}%` : 'unknown'}.`,
        };
      } else {
        return {
          summary:
            "Something unexpected happened in the app. Like when a machine makes an unusual noise - we need to figure out what's wrong.",
          technicalContext: 'General application error - needs investigation',
          userAction:
            "Try the recovery options shown, starting with 'Reset State'. If nothing works, refreshing the page usually helps.",
          sessionContext: `User encountered this error while using ${this.props.componentName || 'unknown component'} on ${window.location.pathname}. Browser: ${navigator.userAgent}. Time: ${new Date().toLocaleString()}.`,
        };
      }
    };

    const leoExplanation = generateLeoExplanation();

    const diagnosticReport = {
      // Leo's Human-Friendly Explanation
      leoExplanation: {
        simpleExplanation: leoExplanation.summary,
        whatUserWasDoing: leoExplanation.sessionContext,
        suggestedUserAction: leoExplanation.userAction,
      },

      // Developer Diagnostic Prompt for AI Assistant
      aiDiagnosticPrompt: `CONTEXT: CondoShield CRM Error Analysis Request
      
ERROR DETAILS:
- Component: ${this.props.componentName || 'Unknown'}
- Error: ${this.state.error?.message}
- Technical Context: ${leoExplanation.technicalContext}
- User Context: ${leoExplanation.sessionContext}
- Recovery Attempts: ${this.state.recoveryAttempts} (all failed)

CURRENT SITUATION:
- The user experienced this error during normal application usage
- Auto-recovery mechanisms did not resolve the issue
- User is seeing an error boundary with recovery options

ANALYSIS REQUEST:
1. Is this a known pattern from recent development work?
2. What specific code changes might have caused this?
3. Is this related to the recent hoisting fixes or other recent changes?
4. What's the most likely root cause and quickest fix?
5. Should this be prioritized as a critical bug?

Please provide specific guidance for resolution.`,

      errorDetails: {
        message: this.state.error?.message,
        stack: this.state.error?.stack,
        componentStack: this.state.errorInfo?.componentStack,
        componentName: this.props.componentName || 'UnknownComponent',
        recoveryAttempts: this.state.recoveryAttempts,
      },
      systemInfo: {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        connection: navigator.connection
          ? {
              effectiveType: navigator.connection.effectiveType,
              downlink: navigator.connection.downlink,
              rtt: navigator.connection.rtt,
            }
          : 'unknown',
      },
      memoryInfo: performance.memory
        ? {
            used: `${(performance.memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
            total: `${(performance.memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
            limit: `${(performance.memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`,
            usage: `${((performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100).toFixed(1)}%`,
          }
        : 'not available',
      storageInfo: {
        localStorage: Object.keys(localStorage).length,
        sessionStorage: Object.keys(sessionStorage).length,
        cookies: document.cookie.split(';').length,
      },
      errorHistory: window.errorMonitor?.errorLog?.slice(-5) || [],
    };

    const reportText = JSON.stringify(diagnosticReport, null, 2);

    navigator.clipboard
      .writeText(reportText)
      .then(() => {
        this.showModalConfirmation(
          '📋 Error Report Copied',
          `Leo's Explanation: ${leoExplanation.summary}

Complete diagnostic information has been copied to your clipboard, including technical details for the development team.

What you can do: ${leoExplanation.suggestedUserAction}`,
          null,
          reportText
        );
      })
      .catch(() => {
        // Fallback for clipboard failure
        this.showModalConfirmation(
          '📋 Error Report Ready',
          `Leo's Explanation: ${leoExplanation.summary}

The diagnostic information couldn't be copied automatically, but you can copy it from the details below.

What you can do: ${leoExplanation.suggestedUserAction}`,
          'Diagnostic information is ready below. Please copy manually:',
          null,
          reportText
        );
      });
  };

  // Show modal confirmation instead of alert
  showModalConfirmation = (title, message, onConfirm = null, details = null, isLoading = false) => {
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 24px;
      max-width: 500px;
      width: 90%;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
      position: relative;
    `;

    const htmlContent = `
      <div style="position: sticky; top: 0; background: white; padding-bottom: 16px; border-bottom: 1px solid #eee; margin-bottom: 16px;">
        <div style="display: flex; justify-content: between; align-items: center;">
          <h3 style="color: #333; margin: 0; font-size: 1.3em; flex: 1;">
            ${title}
          </h3>
          <button onclick="window.modalCloseAction()" style="
            position: absolute;
            top: 0;
            right: 0;
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #999;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: all 0.2s;
          " onmouseover="this.style.background='#f0f0f0'; this.style.color='#333';" onmouseout="this.style.background='none'; this.style.color='#999';">
            ×
          </button>
        </div>
      </div>
      
      <div style="margin-bottom: 20px;">
        ${
          isLoading
            ? `
          <div style="text-align: center; padding: 20px;">
            <div style="
              width: 32px;
              height: 32px;
              border: 3px solid #f3f3f3;
              border-top: 3px solid #2196f3;
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin: 0 auto 16px auto;
            "></div>
            <style>
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
            <p style="color: #666; margin: 0; line-height: 1.5;">
              ${message}
            </p>
          </div>
        `
            : `
          <p style="color: #666; margin: 0; line-height: 1.5;">
            ${message}
          </p>
        `
        }
      </div>
      
      ${
        details && !isLoading
          ? `
        <details style="margin: 16px 0; background: #f5f5f5; padding: 12px; border-radius: 6px;">
          <summary style="cursor: pointer; font-weight: bold; margin-bottom: 8px;">
            📋 Technical Details
          </summary>
          <pre style="font-size: 0.8em; overflow-x: auto; white-space: pre-wrap; color: #444; margin: 8px 0 0 0;">${details}</pre>
        </details>
      `
          : ''
      }
      
      ${
        !isLoading
          ? `
        <div style="display: flex; gap: 12px; justify-content: center; margin-top: 20px; position: sticky; bottom: 0; background: white; padding-top: 16px; border-top: 1px solid #eee;">
          ${
            onConfirm
              ? `
            <button onclick="window.modalConfirmAction()" style="
              padding: 10px 20px;
              background: #4caf50;
              color: white;
              border: none;
              border-radius: 6px;
              cursor: pointer;
              font-weight: bold;
            ">
              Continue
            </button>
          `
              : ''
          }
          <button onclick="window.modalCloseAction()" style="
            padding: 10px 20px;
            background: #2196f3;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: bold;
          ">
            Close
          </button>
        </div>
      `
          : ''
      }
    `;

    content.innerHTML = DOMPurify.sanitize(htmlContent);
    modal.appendChild(content);
    document.body.appendChild(modal);

    // Close on click outside (but not on content)
    modal.addEventListener('click', e => {
      if (e.target === modal) {
        modal.remove();
        if (window.modalCloseAction) {
          delete window.modalCloseAction;
        }
        if (window.modalConfirmAction) {
          delete window.modalConfirmAction;
        }
      }
    });

    // Prevent content clicks from closing modal
    content.addEventListener('click', e => {
      e.stopPropagation();
    });

    // Set up action handlers
    window.modalConfirmAction = () => {
      modal.remove();
      if (onConfirm) onConfirm();
      delete window.modalConfirmAction;
      delete window.modalCloseAction;
    };

    window.modalCloseAction = () => {
      modal.remove();
      delete window.modalConfirmAction;
      delete window.modalCloseAction;
    };

    // Auto-close after 60 seconds (except for loading states)
    if (!isLoading) {
      setTimeout(() => {
        if (document.body.contains(modal)) {
          modal.remove();
          delete window.modalConfirmAction;
          delete window.modalCloseAction;
        }
      }, 60000);
    }
  };

  // Scan for JavaScript errors on current page
  scanForJavaScriptErrors = () => {
    const errors = [];

    // Check console for recent errors (if available)
    if (window.errorMonitorLogs) {
      const recentErrors = window.errorMonitorLogs.slice(-5);
      errors.push(
        ...recentErrors.map(log => ({
          type: 'console',
          message: log.message,
          timestamp: log.timestamp,
          source: log.source,
        }))
      );
    }

    // Check for React DevTools errors
    if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      try {
        const reactErrors = window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot;
        if (reactErrors) {
          errors.push({
            type: 'react-devtools',
            message: 'React DevTools detected - check for component errors',
            timestamp: new Date().toISOString(),
          });
        }
      } catch (error) {
        // Ignore DevTools access errors - React DevTools may not be available
        console.debug('React DevTools access error:', error);
      }
    }

    // Check for network errors in performance entries
    try {
      const networkEntries = performance
        .getEntriesByType('navigation')
        .concat(performance.getEntriesByType('resource'));

      const failedRequests = networkEntries.filter(
        entry => entry.transferSize === 0 && entry.decodedBodySize === 0 && entry.duration > 0
      );

      failedRequests.forEach(request => {
        errors.push({
          type: 'network',
          message: `Failed to load: ${request.name}`,
          timestamp: new Date(performance.timeOrigin + request.startTime).toISOString(),
          source: request.name,
        });
      });
    } catch (error) {
      // Performance API not available in all browsers
      console.debug('Performance API access error:', error);
    }

    // Check for unhandled promise rejections
    if (window.unhandledPromiseRejections) {
      errors.push(
        ...window.unhandledPromiseRejections.slice(-3).map(rejection => ({
          type: 'promise-rejection',
          message: rejection.reason?.message || rejection.reason || 'Unhandled promise rejection',
          timestamp: rejection.timestamp,
          source: 'Promise',
        }))
      );
    }

    return errors;
  };

  render() {
    if (this.state.hasError) {
      const isRecoveryExhausted = this.state.recoveryAttempts >= this.maxRecoveryAttempts;

      // Show error UI with escalated recovery options
      return (
        this.props.fallback || (
          <div
            style={{
              padding: '24px',
              border: '2px solid #f44336',
              borderRadius: '12px',
              backgroundColor: '#fff3f3',
              margin: '20px',
              textAlign: 'center',
              maxWidth: '700px',
              marginLeft: 'auto',
              marginRight: 'auto',
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
            }}
          >
            <h3 style={{ color: '#f44336', margin: '0 0 16px 0', fontSize: '1.2em' }}>
              {isRecoveryExhausted
                ? '🆘 Recovery Escalation Required'
                : '🔧 Component Error - Auto Recovery Active'}
            </h3>

            <p style={{ margin: '0 0 16px 0', color: '#666', lineHeight: 1.5 }}>
              {isRecoveryExhausted
                ? 'Automatic recovery has been attempted multiple times. Choose a recovery strategy below:'
                : `A component error occurred. Auto-recovery attempt ${this.state.recoveryAttempts} of ${this.maxRecoveryAttempts}.`}
            </p>

            {!isRecoveryExhausted ? (
              <div>
                <p style={{ margin: '0 0 16px 0', color: '#2196f3' }}>
                  🔄 Attempting automatic recovery...
                </p>
                <div
                  style={{
                    width: '100%',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '4px',
                    marginBottom: '16px',
                  }}
                >
                  <div
                    style={{
                      width: `${(this.state.recoveryAttempts / this.maxRecoveryAttempts) * 100}%`,
                      height: '8px',
                      backgroundColor: '#2196f3',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }}
                  ></div>
                </div>
              </div>
            ) : (
              <div>
                {/* Error Details */}
                <details
                  style={{
                    marginBottom: '20px',
                    textAlign: 'left',
                    backgroundColor: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                  }}
                >
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#333' }}>
                    🔍 Error Details
                  </summary>
                  <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                    <strong>Error:</strong> {this.state.error?.message || 'Unknown error'}
                    <br />
                    <strong>Component:</strong> {this.props.componentName || 'Unknown'}
                    <br />
                    <strong>Time:</strong> {new Date().toLocaleString()}
                    <br />
                    <strong>Recovery Attempts:</strong> {this.state.recoveryAttempts}
                  </div>
                </details>

                {/* Leo's Expert Analysis - Prominently displayed at top */}
                <div
                  style={{
                    marginBottom: '24px',
                    textAlign: 'left',
                    backgroundColor: '#fff3e0',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '2px solid #ff9800',
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.15)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{ fontSize: '1.5em', marginRight: '12px' }}>🤖</span>
                    <h3 style={{ margin: '0', color: '#e65100', fontSize: '1.2em' }}>
                      Leo's Expert Analysis
                    </h3>
                  </div>

                  {this.state.error?.message?.includes('before initialization') ? (
                    <div style={{ fontSize: '0.95em', color: '#bf360c', lineHeight: 1.6 }}>
                      <p style={{ margin: '0 0 12px 0', fontWeight: 'bold' }}>
                        🎯 <strong>Root Cause:</strong> JavaScript Hoisting Issue
                      </p>
                      <p style={{ margin: '0 0 16px 0' }}>
                        {this.state.error?.message?.includes('handleReactError')
                          ? `The function 'handleReactError' is being called before it's defined. This is a classic JavaScript hoisting problem where a function is referenced in a dependency array before its declaration.`
                          : this.state.error?.message?.includes('attemptAutoRecovery')
                            ? `The function 'attemptAutoRecovery' is being called before it's defined. This is a JavaScript hoisting issue where a function is referenced before its declaration in the component initialization.`
                            : `A function is being accessed before it's initialized. This is a JavaScript hoisting problem where functions are referenced before their declaration.`}
                      </p>

                      <div
                        style={{
                          backgroundColor: '#ffcc80',
                          padding: '12px',
                          borderRadius: '6px',
                          marginBottom: '16px',
                        }}
                      >
                        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#e65100' }}>
                          💡 <strong>Developer Action Required:</strong>
                        </p>
                        <ol style={{ margin: '0', paddingLeft: '20px', color: '#bf360c' }}>
                          {this.state.error?.message?.includes('handleReactError') ? (
                            <>
                              <li>
                                Move <code>handleReactError</code> function definition before the{' '}
                                <code>useEffect</code> that references it
                              </li>
                              <li>
                                Ensure all dependencies are properly declared in useCallback
                                dependency arrays
                              </li>
                              <li>
                                Check for circular dependencies in the component initialization
                              </li>
                            </>
                          ) : this.state.error?.message?.includes('attemptAutoRecovery') ? (
                            <>
                              <li>
                                Move <code>attemptAutoRecovery</code> function definition before{' '}
                                <code>handleReactError</code>
                              </li>
                              <li>
                                Consider using a ref or window object to store the function for
                                delayed access
                              </li>
                              <li>Reorganize function declarations to respect dependency order</li>
                            </>
                          ) : (
                            <>
                              <li>Reorganize function declarations to respect dependency order</li>
                              <li>Move function definitions before their first usage</li>
                              <li>Check for circular dependencies in useCallback hooks</li>
                            </>
                          )}
                        </ol>
                      </div>

                      <p style={{ margin: '0', fontSize: '0.9em', fontStyle: 'italic' }}>
                        This error prevents the component from initializing properly. A page refresh
                        may temporarily resolve it, but the code needs to be fixed to prevent
                        recurrence.
                      </p>
                    </div>
                  ) : this.state.error?.message?.includes('network') ||
                    this.state.error?.message?.includes('failed to fetch') ? (
                    <div style={{ fontSize: '0.95em', color: '#bf360c', lineHeight: 1.6 }}>
                      <p style={{ margin: '0 0 12px 0', fontWeight: 'bold' }}>
                        🌐 <strong>Network Connectivity Issue</strong>
                      </p>
                      <p style={{ margin: '0 0 12px 0' }}>
                        The application cannot reach the server. Check your internet connection and
                        try refreshing the page.
                      </p>
                      <p style={{ margin: '0', fontSize: '0.9em', fontStyle: 'italic' }}>
                        Recommended: Clear cache and refresh, or wait a moment and try again.
                      </p>
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.95em', color: '#bf360c', lineHeight: 1.6 }}>
                      <p style={{ margin: '0 0 12px 0', fontWeight: 'bold' }}>
                        ⚠️ <strong>Unexpected Error Detected</strong>
                      </p>
                      <p style={{ margin: '0 0 12px 0' }}>
                        An unexpected error occurred. Try the recovery options below, starting with
                        "Reset State" for the highest chance of success.
                      </p>
                      <button
                        onClick={() => this.askAIForHelp()}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#2196f3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85em',
                          fontWeight: 'bold',
                        }}
                      >
                        🤖 Get AI Analysis
                      </button>
                    </div>
                  )}
                </div>

                {/* Recovery Strategy Sequence */}
                <div
                  style={{
                    marginBottom: '20px',
                    textAlign: 'left',
                    backgroundColor: '#e8f5e8',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #4caf50',
                  }}
                >
                  <h4 style={{ margin: '0 0 12px 0', color: '#2e7d32', fontSize: '1em' }}>
                    🎯 Recommended Recovery Sequence
                  </h4>
                  <div style={{ fontSize: '0.9em', color: '#2e7d32', lineHeight: 1.6 }}>
                    <div style={{ margin: '4px 0' }}>
                      <strong>1. Reset State</strong> - Clear corrupted data and restart component
                    </div>
                    <div style={{ margin: '4px 0' }}>
                      <strong>2. Clear Cache</strong> - Remove cached data that may be causing
                      conflicts
                    </div>
                    <div style={{ margin: '4px 0' }}>
                      <strong>3. Safe Mode</strong> - Reload with minimal features to isolate the
                      issue
                    </div>
                    <div style={{ margin: '4px 0' }}>
                      <strong>4. Copy Error</strong> - Get diagnostic information for
                      troubleshooting
                    </div>
                  </div>
                </div>

                {/* Recovery Options */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: '12px',
                    marginBottom: '16px',
                  }}
                >
                  <button
                    onClick={() => this.handleAdvancedRecovery('reset-state')}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      fontWeight: 'bold',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    title='Clear all data and restart with fresh state'
                  >
                    <span>🔄</span>
                    <span>Reset State</span>
                    <small style={{ fontSize: '0.8em', opacity: 0.9 }}>Start Fresh</small>
                  </button>

                  <button
                    onClick={() => this.handleAdvancedRecovery('clear-cache')}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      fontWeight: 'bold',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    title='Clear browser cache and reload'
                  >
                    <span>🧹</span>
                    <span>Clear Cache</span>
                    <small style={{ fontSize: '0.8em', opacity: 0.9 }}>Remove Conflicts</small>
                  </button>

                  <button
                    onClick={() => this.handleAdvancedRecovery('safe-mode')}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: '#9c27b0',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      fontWeight: 'bold',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    title='Reload in safe mode with minimal features'
                  >
                    <span>🛡️</span>
                    <span>Safe Mode</span>
                    <small style={{ fontSize: '0.8em', opacity: 0.9 }}>Minimal Features</small>
                  </button>

                  <button
                    onClick={() => this.handleAdvancedRecovery('copy-error')}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: '#607d8b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      fontWeight: 'bold',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    title='Copy diagnostic information to clipboard'
                  >
                    <span>📋</span>
                    <span>Copy Error</span>
                    <small style={{ fontSize: '0.8em', opacity: 0.9 }}>Get Diagnostics</small>
                  </button>
                </div>

                {/* Fallback Option */}
                <div style={{ borderTop: '1px solid #ddd', paddingTop: '16px' }}>
                  <p style={{ margin: '0 0 12px 0', fontSize: '0.9em', color: '#666' }}>
                    If none of the above options work:
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#2196f3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '1em',
                      fontWeight: 'bold',
                    }}
                  >
                    🔄 Force Page Refresh
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundaryWithRecovery;
